'use client';
import styles from './ProductOptionsRadio.module.scss';
import { Entry } from 'contentful';
import { Document } from '@contentful/rich-text-types';

import { removeCentsFromAmount } from '@/utils/currency';
import { ShopifyVariant, ShopifyProduct } from '@/lib/shopify';
import { TypeProduct, TypeProductFields } from '@/core/generated-types';
import { RichText } from '../RichText';
import { RadioButton } from '../RadioButton';
import { formatMoney } from '@/utils/helpers';
import { useRouter } from 'next/router';
import classNames from 'classnames';

interface ProductOptionRadioInterface {
  products: Entry<TypeProductFields>[];
  productSelected: Entry<TypeProductFields>;
  shopifyProducts: ShopifyProduct[];
  onChange: (
    product: TypeProduct,
    shopifyProduct: ShopifyProduct,
    variant: ShopifyVariant,
  ) => void;
}

function ProductOptionRadio({
  products,
  productSelected,
  shopifyProducts,
  onChange,
}: ProductOptionRadioInterface) {
  const { locale } = useRouter();

  return (
    <div className="mb-4" test-id="product-options">
      {products &&
        products.map((product, index) => {
          const id = product.sys.id;
          const shopifyProduct = shopifyProducts[index];
          const variant = shopifyProduct.variants[0];

          // The first variant is always used because the others
          // do not fit into the radio options design proposed
          const formattedPrice = variant.formattedPrice;

          const { title, optionDetails, strikethroughPrice } = product.fields;
          const checked = productSelected?.sys.id === id;

          return (
            <RadioButton
              key={id}
              id={id}
              Content={() => (
                <div className="flex-auto">
                  <div className="flex items-center">
                    <div className="text-[1.25rem] pr-8 flex-auto">{title}</div>
                    <div className="flex flex-none items-center">
                      {strikethroughPrice && (
                        <div className="text-lg line-through font-normal text-[#6E757C] mr-1">
                          {formatMoney(strikethroughPrice, locale, true)}
                        </div>
                      )}
                      <div
                        className={classNames(
                          'text-2xl',
                          !!strikethroughPrice && 'text-purple',
                        )}
                      >
                        {formattedPrice &&
                          removeCentsFromAmount(formattedPrice)}
                      </div>
                    </div>
                  </div>
                  {optionDetails && (
                    <div className="font-normal text-base mt-4 -ml-9 rte">
                      <RichText
                        content={optionDetails as Document}
                        addParagraphMargins={false}
                      />
                    </div>
                  )}
                </div>
              )}
              className={styles.optionRadio}
              checked={checked}
              onChange={() => {
                onChange(product, shopifyProduct, variant);
              }}
              boldLabel
              badge={product.fields.translations?.fields.badgeStandalone}
            />
          );
        })}
    </div>
  );
}

export default ProductOptionRadio;
